// client/src/pages/AIReview/AIReviewChatPage.tsx
// AI return chat interface.
// URL: /ai-review/chat             — wizard flow (client → year → return)
// URL: /ai-review/chat?returnId=X  — direct link, skips wizard

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Search, Loader, Send, RefreshCw, Zap, Database, Printer, ZoomIn, MessageSquare, Mic, Paperclip, X, FileText, Image, GitCompare, Share2, Mail, FileDown, Paintbrush } from 'lucide-react';
import { usePermissions } from '../../contexts/PermissionsContext';
import { PERMISSIONS } from '../../types/permissions';
import { useAuth } from '../../auth/AuthContext';

import {
  lookupReturns, loadReturn, sendMessage, uploadChatFile, loadCrossRef,
  shareChat, loadSharedChat, generateDeliverablePdf as generatePdfApi,
  polishSnippets, ReturnInUseError,
  type ChatMessage, type ChatReturn, type ReturnMeta, type ChatAttachment,
} from '../../services/aiReviewChat';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PEClient {
  clientCode:  string;
  clientName:  string;
  partnerName: string;
  managerName: string;
}

type WizardStep = 'client' | 'year' | 'return' | 'loading' | 'chat';

// ── Constants ─────────────────────────────────────────────────────────────────

const TAX_YEARS: number[] = (() => {
  const cur = new Date().getFullYear() - 1; // default to prior year
  return Array.from({ length: 6 }, (_, i) => cur - i);
})();

// entityTypes: if set, only show for returns whose entityType letter is in this set (CCH codes: I=individual, F=fiduciary/trust, C=corp, S=s-corp, P=partnership, X=exempt)
const POWER_PROMPTS: Array<{ label: string; prompt: string; entityTypes?: string[] }> = [
  {
    label: 'Value Talking Points',
    prompt: 'Create a list of specific talking points I can use in a client meeting to demonstrate the tax value we\'ve delivered this year. For each point, reference the specific strategy or tax code provision we used (e.g., Section 179, bonus depreciation, QBI deduction, retirement plan contributions, entity structure benefits) and quantify the estimated tax savings where the data allows. The goal is to help the client clearly understand our expertise and the tangible benefit of proactive tax planning.',
  },
  {
    label: 'Risk & Audit Flags',
    prompt: 'Review this return for any items that could draw IRS scrutiny or represent risk areas worth discussing with the client. Consider: unusually high deductions relative to income, aggressive positions or elections, large charitable contributions, significant NOLs, related party transactions, home office or vehicle deductions, and any items that deviate from norms for this entity type and industry. Summarize each risk with a brief explanation of why it could be flagged and any recommended mitigation.',
  },
  {
    label: 'Next Year Planning',
    prompt: 'Based on what you see in this year\'s return, identify the top tax planning opportunities to act on before year-end or discuss in early planning for next year. Consider: timing of income and deductions, retirement plan contributions and new plan options, bonus depreciation and Section 179 elections, estimated tax adjustments, entity structure optimization, expiring provisions, and any elections that were missed this year but could be implemented next year.',
  },
  {
    label: 'Extension Review',
    prompt: `Compare the prior year return to the current year return and perform an extension readiness and completeness analysis. The goal is to determine whether we are paying the appropriate amount with the extension, given that we may not have final numbers for everything yet.

Produce the following:

## 1. Completeness Check Table

Create a table with these columns:

| Income/Deduction Source | Prior Year Amount | Current Year Amount | Status | Notes |
|---|---|---|---|---|

For **Status**, use one of:
- **Present** — item appears in both years, amounts look reasonable
- **Missing** — item was in prior year but is absent or zero in current year (flag for follow-up)
- **New** — item appears in current year but not prior year
- **Estimate Needed** — item is present but the amount looks incomplete, placeholder-like, or significantly lower than expected

Cover all material income sources (W-2, 1099-INT, 1099-DIV, 1099-B, K-1s, rental, business, farm, retirement distributions, Social Security, etc.), major deductions (itemized components, business expenses, retirement contributions, HSA), credits, and state items.

## 2. Payment Analysis

- What is the projected current year tax liability based on available data?
- What estimated/withholding payments have already been made?
- What is the estimated balance due?
- Does the safe harbor (100% or 110% of prior year tax) appear to be met?
- Recommend a specific extension payment amount with reasoning.

## 3. Risk Items for Extension

Flag anything that could materially change the liability once final numbers arrive (e.g., missing K-1s, pending asset sales, unknown state filing obligations, items that are clearly estimates).

## 4. Extension Readiness Score

Assign a confidence score from 1-10 for extension readiness:
- **8-10**: All major items accounted for, safe harbor likely met, low risk of underpayment penalty
- **5-7**: Most items present but some gaps that could shift liability meaningfully
- **1-4**: Significant missing information, high risk of underpayment

Provide the score with a brief justification.`,
  },
  {
    label: 'Business Advisory',
    prompt: 'Summarize the key financial and tax-relevant details from this return through a business advisory lens. What does the return reveal about the client\'s financial health, profitability trends, owner compensation vs. distributions, and business model? Identify anything that should prompt a deeper conversation about their operations, growth trajectory, or ownership structure. Frame your response as preparation notes for a client advisory meeting.',
    entityTypes: ['C', 'S', 'P'],
  },
  {
    label: 'Transaction Readiness',
    prompt: 'Assess this return for indicators relevant to a potential business sale, ownership transition, or succession event. Consider: balance sheet strength and accumulated earnings, owner compensation relative to distributions, entity structure implications for a taxable sale vs. asset sale, built-in gains exposure, installment sale opportunities, and any signals that the owner may be approaching an exit or transition. Provide a concise transaction advisory perspective with any planning considerations if a sale were to occur in the next 1–3 years.',
    entityTypes: ['C', 'S', 'P'],
  },
];

// ── Styles ────────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  page:       { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },
  header:     { padding: '12px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(0,0,0,.12)', flexShrink: 0 },
  botAvatar:  { width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 },
  headerInfo: { flex: 1, minWidth: 0 },
  headerTitle:{ fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  headerSub:  { fontSize: 12, color: 'var(--muted)' },
  body:       { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' },

  // Wizard
  wizard:     { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', gap: 24 },
  card:       { background: 'linear-gradient(180deg,rgba(255,255,255,.04),rgba(255,255,255,.02))', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '28px 32px', width: '100%', maxWidth: 480, boxShadow: 'var(--shadow)' },
  cardTitle:  { fontWeight: 700, fontSize: 17, marginBottom: 4 },
  cardSub:    { fontSize: 13, color: 'var(--muted)', marginBottom: 20 },
  input:      { width: '100%', padding: '9px 11px', borderRadius: 10, border: '1px solid var(--border)', background: 'rgba(0,0,0,.18)', color: 'var(--text)', fontSize: 14, outline: 'none', boxSizing: 'border-box' as const },
  resultList: { marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 280, overflowY: 'auto' as const },
  resultItem: { padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border)', background: 'rgba(255,255,255,.02)', cursor: 'pointer', transition: 'border-color .15s', fontSize: 14 },
  yearGrid:   { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 4 },
  yearBtn:    { padding: '10px 0', textAlign: 'center' as const, borderRadius: 10, border: '1px solid var(--border)', cursor: 'pointer', fontSize: 14, color: 'var(--text)', background: 'rgba(255,255,255,.02)', fontFamily: 'inherit', transition: 'all .15s' },
  yearBtnSel: { background: 'rgba(122,162,255,.18)', borderColor: 'rgba(122,162,255,.65)', color: 'var(--accent)' },
  btn:        { padding: '9px 11px', borderRadius: 10, border: '1px solid rgba(122,162,255,.65)', background: 'rgba(122,162,255,.18)', color: 'var(--accent)', fontWeight: 600, fontSize: 14, cursor: 'pointer', width: '100%', marginTop: 12, fontFamily: 'inherit' },
  btnSm:      { padding: '7px 13px', borderRadius: 10, border: '1px solid var(--border)', background: 'rgba(255,255,255,.03)', color: 'var(--text)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' },
  error:      { color: 'var(--bad)', fontSize: 13, marginTop: 8 },

  // Loading state
  loadingBox:    { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 },
  loadingAvatar: { width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' as const },
  loadingText:   { fontSize: 15, color: 'var(--muted)' },

  // Chat
  messages:     { flex: 1, overflowY: 'auto' as const, padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 16 },
  msgRow:       { display: 'flex', gap: 10, alignItems: 'flex-start', maxWidth: 820, width: '100%' },
  msgRowUser:   { alignSelf: 'flex-end', flexDirection: 'row-reverse' as const },
  msgBubble:    { background: 'linear-gradient(180deg,rgba(255,255,255,.04),rgba(255,255,255,.02))', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 16px', fontSize: 14, lineHeight: 1.6, minWidth: 0, flex: 1 },
  msgBubbleUser:{ background: 'rgba(122,162,255,.16)', border: '1px solid rgba(122,162,255,.35)', borderRadius: 'var(--radius)', color: 'var(--text)' },
  msgAvatar:    { width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' as const, flexShrink: 0, marginTop: 2 },
  scopeTag:     { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--muted)', marginBottom: 6, fontStyle: 'italic' },
  thinkingDot:  { display: 'inline-block', animation: 'chatDotPulse 1.4s infinite', fontSize: 22, letterSpacing: 2, color: 'var(--muted)' },

  // Power prompts strip
  promptStrip: { padding: '6px 16px 6px', display: 'flex', gap: 6, overflowX: 'auto' as const, flexShrink: 0, scrollbarWidth: 'none' as const },
  promptBtn:   { padding: '5px 12px', borderRadius: 20, border: '1px solid var(--border)', background: 'rgba(255,255,255,.04)', color: 'var(--muted)', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' as const, fontFamily: 'inherit', flexShrink: 0, transition: 'all .15s' },

  // Input bar
  inputBar:  { borderTop: '1px solid var(--border)', padding: '12px 16px', display: 'flex', gap: 8, alignItems: 'flex-end', background: 'rgba(0,0,0,.12)', flexShrink: 0 },
  textarea:  { flex: 1, padding: '9px 11px', borderRadius: 10, border: '1px solid var(--border)', background: 'rgba(0,0,0,.18)', color: 'var(--text)', fontSize: 14, resize: 'none' as const, outline: 'none', lineHeight: 1.5, minHeight: 42, maxHeight: 220, fontFamily: 'inherit', overflow: 'auto' as const },
  sendBtn:   { padding: '9px 13px', borderRadius: 10, border: '1px solid rgba(122,162,255,.65)', background: 'rgba(122,162,255,.18)', color: 'var(--accent)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, alignSelf: 'flex-end' as const },
  micBtn:    { padding: '9px 11px', borderRadius: 10, border: '1px solid var(--border)', background: 'rgba(255,255,255,.04)', color: 'var(--muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, alignSelf: 'flex-end' as const, transition: 'all .15s' },
  micBtnOn:  { border: '1px solid rgba(239,68,68,.6)', background: 'rgba(239,68,68,.15)', color: '#ef4444', animation: 'micPulse 1.5s ease-out infinite' },
  modeToggle:{ display: 'flex', gap: 4, flexShrink: 0, alignSelf: 'flex-end' as const },
  modeBtn:   { padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'rgba(255,255,255,.03)', color: 'var(--muted)', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'inherit' },
  modeBtnOn: { background: 'rgba(122,162,255,.15)', borderColor: 'rgba(122,162,255,.55)', color: 'var(--accent)' },
};

// ── CSS injection for markdown + animations ────────────────────────────────────

const SILENCE_MS = 3000; // auto-stop after 3s of no new words

const CSS = `
@keyframes chatDotPulse { 0%,80%,100%{opacity:.2} 40%{opacity:1} }
@keyframes botPensive { 0%,100%{transform:scale(1)} 50%{transform:scale(1.08)} }
@keyframes micPulse { 0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,.5)} 60%{box-shadow:0 0 0 6px rgba(239,68,68,0)} }
@keyframes tipFadeIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
.paint-mode-active .chat-md{cursor:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%236CC24A' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m14.622 17.897-10.68-2.913'/%3E%3Cpath d='M18.376 2.622a1 1 0 1 1 3.002 3.002L17.36 9.643a.5.5 0 0 0 0 .707l.944.944a2.41 2.41 0 0 1 0 3.408l-.944.944a.5.5 0 0 1-.707 0L8.354 7.348a.5.5 0 0 1 0-.707l.944-.944a2.41 2.41 0 0 1 3.408 0l.944.944a.5.5 0 0 0 .707 0z'/%3E%3Cpath d='M9 8c-1.804 2.71-3.97 3.46-6.583 3.948a.507.507 0 0 0-.302.819l7.32 8.883a1 1 0 0 0 1.185.204C12.735 20.405 16 16.792 16 15'/%3E%3C/svg%3E") 2 22, crosshair}
.paint-mode-active .chat-md ::selection{background:rgba(108,194,74,.4)}
.paint-highlight{background:rgba(108,194,74,.15);border-left:3px solid #6CC24A;padding:4px 8px;margin:2px 0;border-radius:0 4px 4px 0}
.chat-action-btn{transition:all .15s ease}
.chat-action-btn:hover:not(:disabled){background:rgba(255,255,255,.1)!important;border-color:rgba(122,162,255,.5)!important;color:var(--accent)!important;transform:scale(1.06)}
.chat-action-btn:active:not(:disabled){transform:scale(.92);background:rgba(122,162,255,.2)!important}
.chat-send-btn{transition:all .15s ease}
.chat-send-btn:hover:not(:disabled){background:rgba(122,162,255,.3)!important;transform:scale(1.06)}
.chat-send-btn:active:not(:disabled){transform:scale(.92)}
.bot-pensive { animation: botPensive 1.2s ease-in-out infinite; }
.chat-md h1,.chat-md h2,.chat-md h3{font-weight:700;margin:14px 0 6px}
.chat-md h2{font-size:1.1em;color:var(--accent);border-bottom:1px solid var(--border);padding-bottom:4px}
.chat-md h3{font-size:1em;color:var(--accent2)}
.chat-md p{margin:0 0 8px}
.chat-md ul,.chat-md ol{padding-left:1.5em;margin:0 0 8px}
.chat-md li{margin:2px 0}
.chat-md table{border-collapse:collapse;width:100%;font-size:13px;margin:10px 0}
.chat-md th{background:rgba(0,0,0,.18);font-weight:650;color:var(--muted);text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.05em}
.chat-md th,.chat-md td{border:1px solid var(--border);padding:7px 10px}
.chat-md tr:nth-child(even) td{background:rgba(255,255,255,.02)}
.chat-md code{background:rgba(0,0,0,.25);padding:.1em .35em;border-radius:4px;font-size:85%;color:var(--accent2)}
.chat-md pre{background:rgba(0,0,0,.3);border:1px solid var(--border);border-radius:10px;padding:10px 14px;overflow-x:auto}
.chat-md pre code{background:none;padding:0;color:var(--text)}
.chat-md a{color:var(--accent)}
`;

// ── Component ─────────────────────────────────────────────────────────────────

export default function AIReviewChatPage() {
  const { hasPermission, ready } = usePermissions();
  const { ready: authReady, email: authEmail } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const directReturnId = searchParams.get('returnId');
  const sharedChatId   = searchParams.get('shared');

  const [step,        setStep]        = useState<WizardStep>(directReturnId ? 'loading' : 'client');
  const [clientQuery, setClientQuery] = useState('');
  const [clients,     setClients]     = useState<PEClient[]>([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientError, setClientError] = useState('');
  const [selectedClient, setSelectedClient] = useState<PEClient | null>(null);
  const [selectedYear,   setSelectedYear]   = useState<number>(TAX_YEARS[0]);
  const [returns,        setReturns]        = useState<ChatReturn[]>([]);
  const [returnsLoading, setReturnsLoading] = useState(false);
  const [returnsError,   setReturnsError]   = useState('');
  const [loadError,      setLoadError]      = useState('');

  const [returnId,      setReturnId]      = useState<string>(directReturnId || '');
  const [selectedReturn, setSelectedReturn] = useState<ChatReturn | null>(null);
  const [returnMeta, setReturnMeta] = useState<ReturnMeta>({});
  const [viewNames,  setViewNames]  = useState<string[]>([]);
  const [messages,   setMessages]   = useState<ChatMessage[]>([]);
  const [input,      setInput]      = useState('');
  const [sending,      setSending]      = useState(false);
  const [introLoading, setIntroLoading] = useState(false);
  const [sendError,    setSendError]    = useState('');
  const [mode,         setMode]         = useState<'auto' | 'full'>('auto');
  const [lastScoped,   setLastScoped]   = useState<string[] | null>(null);
  const [lastPriorYear, setLastPriorYear] = useState(false);

  const [selPopup,     setSelPopup]     = useState<{ text: string; x: number; y: number; msgIdx?: number } | null>(null);
  const [isListening,  setIsListening]  = useState(false);
  const [interimText,  setInterimText]  = useState('');
  const [micSupported, setMicSupported] = useState(true);
  const [attachments,    setAttachments]    = useState<ChatAttachment[]>([]);
  const [uploadingFile,  setUploadingFile]  = useState(false);
  const [uploadError,    setUploadError]    = useState('');
  const [toast,          setToast]          = useState<string | null>(null);
  const [removeConfirm,  setRemoveConfirm]  = useState<number | null>(null);
  const [quoteContext,   setQuoteContext]   = useState<string | null>(null);

  // Cross-ref modal state
  const [crossRefOpen,     setCrossRefOpen]     = useState(false);
  const [crQuery,          setCrQuery]          = useState('');
  const [crClients,        setCrClients]        = useState<PEClient[]>([]);
  const [crClientLoading,  setCrClientLoading]  = useState(false);
  const [crSelectedClient, setCrSelectedClient] = useState<PEClient | null>(null);
  const [crYear,           setCrYear]           = useState<number>(TAX_YEARS[0]);
  const [crReturns,        setCrReturns]        = useState<ChatReturn[]>([]);
  const [crReturnsLoading, setCrReturnsLoading] = useState(false);
  const [crStep,           setCrStep]           = useState<'client' | 'year' | 'return' | 'loading'>('client');
  const [crError,          setCrError]          = useState('');

  // Share
  const [shareExpanded,   setShareExpanded]   = useState(false);
  const [shareModalOpen,  setShareModalOpen]  = useState(false);
  const [shareTo,         setShareTo]         = useState('');
  const [shareSubject,    setShareSubject]    = useState('');
  const [shareBody,       setShareBody]       = useState('');
  const [shareSending,    setShareSending]    = useState(false);
  const [shareError,      setShareError]      = useState('');
  const [shareSuccess,    setShareSuccess]    = useState(false);
  const [readOnly,        setReadOnly]        = useState(false);

  // Client deliverable
  const [paintMode,       setPaintMode]       = useState(false);
  const [paintSnippets,   setPaintSnippets]   = useState<string[]>([]);
  const [paintHighlights, setPaintHighlights] = useState<Array<{ msgIdx: number; text: string }>>([]);
  const [delivExpanded,   setDelivExpanded]   = useState(false);

  const messagesEndRef  = useRef<HTMLDivElement>(null);
  const debounceRef     = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textareaRef     = useRef<HTMLTextAreaElement>(null);
  const selPopupRef     = useRef<HTMLDivElement>(null);
  const recognitionRef  = useRef<any>(null);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileInputRef    = useRef<HTMLInputElement>(null);
  const toastTimerRef   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const crDebounceRef   = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Inject CSS once
  useEffect(() => {
    const id = 'ai-chat-styles';
    if (!document.getElementById(id)) {
      const el = document.createElement('style');
      el.id = id;
      el.textContent = CSS;
      document.head.appendChild(el);
    }
  }, []);

  // Auto-scroll messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  // Apply paint highlights in the DOM
  useEffect(() => {
    // Clear all existing highlights
    document.querySelectorAll('mark.paint-mark').forEach(m => {
      const parent = m.parentNode;
      if (parent) {
        parent.replaceChild(document.createTextNode(m.textContent || ''), m);
        parent.normalize(); // merge adjacent text nodes
      }
    });
    if (!paintMode || paintHighlights.length === 0) return;

    // For each highlight, find the text in the corresponding message DOM and wrap it
    paintHighlights.forEach(({ msgIdx, text }) => {
      const msgEl = document.querySelector(`[data-msg-idx="${msgIdx}"] .chat-md`);
      if (!msgEl) return;

      const walker = document.createTreeWalker(msgEl, NodeFilter.SHOW_TEXT);
      const textNodes: Text[] = [];
      while (walker.nextNode()) textNodes.push(walker.currentNode as Text);

      // Build the full text and find the selection within it
      const fullText = textNodes.map(n => n.textContent || '').join('');
      const selNorm = text.replace(/\s+/g, ' ').trim();
      const startIdx = fullText.replace(/\s+/g, ' ').indexOf(selNorm);
      if (startIdx === -1) return;

      // Map character index back to text nodes and wrap
      let charCount = 0;
      let started = false;
      let remaining = selNorm.length;
      const normCharAt = (fullStr: string, idx: number) => {
        let ni = 0;
        for (let i = 0; i < fullStr.length; i++) {
          if (/\s/.test(fullStr[i]) && (i === 0 || /\s/.test(fullStr[i - 1]))) continue;
          if (ni === idx) return i;
          ni++;
        }
        return fullStr.length;
      };
      // Simplified: just mark the first text node that contains the start of the selection
      for (const node of textNodes) {
        const nodeText = (node.textContent || '').replace(/\s+/g, ' ');
        const nodeNorm = nodeText.trim();
        if (!started) {
          const normFull = fullText.replace(/\s+/g, ' ');
          const nodeStart = normFull.indexOf(nodeNorm, charCount);
          if (nodeStart !== -1 && nodeStart <= startIdx && nodeStart + nodeNorm.length > startIdx) {
            started = true;
          }
        }
        if (started && remaining > 0 && node.parentNode && node.parentNode.nodeName !== 'MARK') {
          const mark = document.createElement('mark');
          mark.className = 'paint-mark';
          mark.style.cssText = 'background:rgba(108,194,74,.18);border-left:2px solid #6CC24A;padding:1px 2px;border-radius:2px';
          node.parentNode.replaceChild(mark, node);
          mark.appendChild(node);
          remaining -= nodeNorm.length;
        }
        charCount += nodeNorm.length + 1;
      }
    });
  }, [paintHighlights, paintMode]);

  // Auto-grow textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 220) + 'px';
  }, [input, interimText]);

  // Direct returnId load — wait until auth is confirmed before hitting the API
  useEffect(() => {
    if (directReturnId && authReady && authEmail) doLoad(directReturnId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directReturnId, authReady, authEmail]);

  // Shared chat load — read-only mode
  useEffect(() => {
    if (!sharedChatId || !authReady || !authEmail) return;
    setStep('loading');
    loadSharedChat(sharedChatId)
      .then(data => {
        setReturnMeta(data.returnMeta || {});
        setMessages(data.messages || []);
        setReadOnly(true);
        setStep('chat');
      })
      .catch(() => {
        setLoadError('Shared chat not found or has expired.');
        setStep('client');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedChatId, authReady, authEmail]);

  // Dismiss selection popup when clicking outside it
  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (selPopupRef.current && !selPopupRef.current.contains(e.target as Node)) {
        setSelPopup(null);
      }
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  // Cross-ref client search debounce
  useEffect(() => {
    if (!crossRefOpen) return;
    if (crDebounceRef.current) clearTimeout(crDebounceRef.current);
    crDebounceRef.current = setTimeout(() => {
      const q = crQuery;
      if (q.length < 3) { setCrClients([]); return; }
      setCrClientLoading(true);
      setCrError('');
      fetch(`/api/pe-client-search?q=${encodeURIComponent(q)}`)
        .then(r => r.json())
        .then(data => setCrClients(data.clients ?? []))
        .catch(() => setCrError('Search failed.'))
        .finally(() => setCrClientLoading(false));
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crQuery, crossRefOpen]);

  // ── Client search ────────────────────────────────────────────────────────

  const searchClients = useCallback(async (q: string) => {
    if (q.length < 3) { setClients([]); return; }
    setClientLoading(true);
    setClientError('');
    try {
      const res  = await fetch(`/api/pe-client-search?q=${encodeURIComponent(q)}`);
      const data = await res.json();
      setClients(data.clients ?? []);
    } catch { setClientError('Search failed.'); }
    finally  { setClientLoading(false); }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchClients(clientQuery), 300);
  }, [clientQuery, searchClients]);

  if (ready && !hasPermission(PERMISSIONS.NAV_AI_CHAT)) {
    return <Navigate to="/access-denied" replace />;
  }

  function handleClientSelect(c: PEClient) {
    setSelectedClient(c);
    setStep('year');
  }

  // ── Year + return lookup ──────────────────────────────────────────────────

  async function handleYearConfirm() {
    if (!selectedClient) return;
    setReturnsLoading(true);
    setReturnsError('');
    try {
      const list = await lookupReturns(selectedClient.clientCode, String(selectedYear));
      if (list.length === 0) {
        setReturnsError(`No returns found for ${selectedYear}.`);
        return;
      }
      if (list.length === 1) {
        // Auto-select single return
        setSelectedReturn(list[0]);
        setStep('loading');
        await doLoad(list[0].returnId);
      } else {
        setReturns(list);
        setStep('return');
      }
    } catch (e: any) {
      setReturnsError(e.message || 'Lookup failed.');
    } finally {
      setReturnsLoading(false);
    }
  }

  // ── Load return ───────────────────────────────────────────────────────────

  async function doLoad(rid: string) {
    setStep('loading');
    setLoadError('');
    try {
      const result = await loadReturn(rid);
      setReturnId(result.returnId);
      setReturnMeta(result.returnMeta);
      setViewNames(result.viewNames);
      setMessages([]);
      setAttachments([]);
      setUploadError('');
      setStep('chat');

      // Update URL so it's shareable / bookmarkable
      setSearchParams({ returnId: result.returnId }, { replace: true });

      // Auto-fire intro analysis — scoped to 2-year comparison, no user message needed
      setIntroLoading(true);
      setSending(true);
      try {
        const intro = await sendMessage(result.returnId, [], 'auto', true);
        setMessages([{ role: 'assistant', content: intro.content }]);
      } catch {
        // silently fail — chat is still fully usable without the intro
      } finally {
        setIntroLoading(false);
        setSending(false);
      }
    } catch (e: any) {
      if (e instanceof ReturnInUseError) {
        const who = e.lastUpdatedBy || 'another user';
        setLoadError(`This return is currently open in CCH by ${who}. Ask them to close it, then try again.`);
      } else {
        setLoadError(e.message || 'Failed to load return.');
      }
      setStep(directReturnId ? 'client' : 'return');
    }
  }

  // ── Send message ──────────────────────────────────────────────────────────

  async function doSend(text: string) {
    if (!text || sending) return;
    const fullText = quoteContext
      ? `Regarding the following from your analysis:\n\n"${quoteContext}"\n\n${text}`
      : text;
    setQuoteContext(null);
    const newMessages: ChatMessage[] = [...messages, { role: 'user', content: fullText }];
    setMessages(newMessages);
    setLastScoped(null);
    setLastPriorYear(false);
    setSendError('');
    setSending(true);
    try {
      const result = await sendMessage(returnId, newMessages, mode, false, attachments);
      setMessages(prev => [...prev, { role: 'assistant', content: result.content }]);
      if (result.scopedViews) setLastScoped(result.scopedViews);
      setLastPriorYear(!!result.priorYearIncluded);
    } catch (e: any) {
      setSendError(e.message || 'Failed to send message.');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setSending(false);
    }
  }

  async function handleSend() {
    const text = input.trim();
    if (!text) return;
    setInput('');
    await doSend(text);
  }

  // ── File attachments ──────────────────────────────────────────────────────

  const MAX_ATTACHMENTS = 3;
  const ACCEPTED_FILES = '.pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.gif,.webp';

  function showToast(msg: string) {
    setToast(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 4000);
  }

  async function doUpload(file: File) {
    if (attachments.length >= MAX_ATTACHMENTS) {
      showToast(`Maximum ${MAX_ATTACHMENTS} files — remove one to add another.`);
      return;
    }
    setUploadError('');
    setUploadingFile(true);
    try {
      const att = await uploadChatFile(file);
      setAttachments(prev => {
        const next = [...prev, att];
        if (next.length >= MAX_ATTACHMENTS) {
          showToast(`${MAX_ATTACHMENTS}/${MAX_ATTACHMENTS} files attached. Remove one to add another.`);
        }
        return next;
      });
    } catch (err: any) {
      setUploadError(err.message || 'Upload failed.');
    } finally {
      setUploadingFile(false);
    }
  }

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) doUpload(file);
  }

  function handlePaste(e: React.ClipboardEvent) {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const blob = item.getAsFile();
        if (blob) {
          const ext = item.type.split('/')[1] || 'png';
          const file = new File([blob], `screenshot-${Date.now()}.${ext}`, { type: item.type });
          doUpload(file);
        }
        return;
      }
    }
    // If no image found, let normal text paste proceed
  }

  function removeAttachment(idx: number) {
    setRemoveConfirm(idx);
  }

  function confirmRemoveAttachment() {
    if (removeConfirm === null) return;
    setAttachments(prev => prev.filter((_, i) => i !== removeConfirm));
    setUploadError('');
    setRemoveConfirm(null);
  }

  // ── Cross-ref modal ───────────────────────────────────────────────────────

  function openCrossRef() {
    if (attachments.length >= MAX_ATTACHMENTS) {
      showToast(`Maximum ${MAX_ATTACHMENTS} files — remove one to add a cross-reference.`);
      return;
    }
    setCrossRefOpen(true);
    setCrStep('client');
    setCrQuery('');
    setCrClients([]);
    setCrSelectedClient(null);
    setCrReturns([]);
    setCrError('');
  }

  function closeCrossRef() {
    setCrossRefOpen(false);
  }

  // ── Share ─────────────────────────────────────────────────────────────────

  function openShareModal() {
    const clientName = selectedClient?.clientName || returnMeta['RETURN'] || 'Tax Return';
    setShareTo('');
    setShareSubject(`Shared AI Chat: ${clientName}`);
    setShareBody('Please review the attached AI return chat. This is a read-only view for reference.');
    setShareError('');
    setShareSuccess(false);
    setShareExpanded(false);
    setShareModalOpen(true);
  }

  async function handleShareSend() {
    if (!shareTo.trim()) { setShareError('Recipient email is required.'); return; }
    setShareError('');
    setShareSending(true);
    try {
      await shareChat({
        messages,
        returnMeta,
        to:      shareTo.trim(),
        subject: shareSubject,
        body:    shareBody,
        bcc:     authEmail || '',
      });
      setShareSuccess(true);
      setTimeout(() => { setShareModalOpen(false); setShareSuccess(false); }, 2000);
    } catch (e: any) {
      setShareError(e.message || 'Failed to share.');
    } finally {
      setShareSending(false);
    }
  }

  async function crConfirmYear() {
    if (!crSelectedClient) return;
    setCrReturnsLoading(true);
    setCrError('');
    try {
      const list = await lookupReturns(crSelectedClient.clientCode, String(crYear));
      if (list.length === 0) { setCrError(`No returns found for ${crYear}.`); return; }
      if (list.length === 1) {
        await crLoadReturn(list[0].returnId);
      } else {
        setCrReturns(list);
        setCrStep('return');
      }
    } catch (e: any) { setCrError(e.message || 'Lookup failed.'); }
    finally { setCrReturnsLoading(false); }
  }

  async function crLoadReturn(rid: string) {
    setCrStep('loading');
    setCrError('');
    try {
      const att = await loadCrossRef(rid);
      setAttachments(prev => [...prev, att]);
      closeCrossRef();
    } catch (e: any) {
      setCrError(e.message || 'Failed to load cross-reference return.');
      setCrStep('return');
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  // ── Voice input ───────────────────────────────────────────────────────────

  function startListening() {
    const SR: any = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) { setMicSupported(false); return; }

    const recognition = new SR();
    recognition.continuous     = true;
    recognition.interimResults = true;
    recognition.lang           = 'en-US';

    recognition.onresult = (event: any) => {
      // Reset silence timer on every new word
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = setTimeout(() => recognition.stop(), SILENCE_MS);

      let interim = '';
      let finalChunk = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalChunk += t;
        else interim += t;
      }
      if (finalChunk) {
        setInput(prev => {
          const spacer = prev && !prev.endsWith(' ') && !prev.endsWith('\n') ? ' ' : '';
          return prev + spacer + finalChunk;
        });
      }
      setInterimText(interim);
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimText('');
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      recognitionRef.current = null;
      // Re-focus textarea so user can review / edit / send
      setTimeout(() => textareaRef.current?.focus(), 50);
    };

    recognition.onerror = (event: any) => {
      if (event.error === 'not-allowed') setMicSupported(false);
      setIsListening(false);
      setInterimText('');
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
    // Safety stop if mic opens but nothing is said for 10s
    silenceTimerRef.current = setTimeout(() => recognition.stop(), 10_000);
  }

  function stopListening() {
    if (recognitionRef.current) recognitionRef.current.stop();
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
  }

  function handleMicClick() {
    if (isListening) stopListening();
    else startListening();
  }

  // ── Text selection popup ──────────────────────────────────────────────────

  function handleMessagesMouseUp() {
    setTimeout(() => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) { setSelPopup(null); return; }
      const text = sel.toString().trim();
      if (!text || text.length < 5) { setSelPopup(null); return; }
      // Only show for selections inside a bot message
      const anchor = sel.anchorNode?.parentElement;
      const chatMd = anchor?.closest('.chat-md');
      if (!chatMd) { setSelPopup(null); return; }
      // Find the message index from the parent [data-msg-idx] attribute
      const msgRow = chatMd.closest('[data-msg-idx]');
      const msgIdx = msgRow ? parseInt(msgRow.getAttribute('data-msg-idx') || '', 10) : undefined;
      const rect = sel.getRangeAt(0).getBoundingClientRect();
      setSelPopup({ text, x: rect.left + rect.width / 2, y: rect.top, msgIdx });
    }, 10);
  }

  function handleDigIn(text: string) {
    setSelPopup(null);
    window.getSelection()?.removeAllRanges();
    doSend(
      `Regarding the following from your analysis:\n\n"${text}"\n\n` +
      `Please dig deeper into this. Include:\n` +
      `- A prior year vs. current year comparison table for any relevant figures\n` +
      `- The specific line items or schedules driving this\n` +
      `- Why this matters to the client (tax impact, planning implications)\n` +
      `- Any recommended action items or questions to ask the client`
    );
  }

  function handleAskAbout(text: string) {
    setSelPopup(null);
    window.getSelection()?.removeAllRanges();
    setQuoteContext(text);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }

  // ── Print / PDF export ────────────────────────────────────────────────────

  function printChat(mode: 'all' | number) {
    const title   = selectedClient?.clientName || returnMeta['RETURN'] || returnId;
    const subLine = [selectedReturn?.entityType, selectedReturn?.taxYear, selectedClient?.clientCode].filter(Boolean).join(' · ');
    const now     = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const isSingle = mode !== 'all';

    // Single response = just that one bot message; full chat = all messages
    const msgsToprint = isSingle
      ? [messages[mode as number]]
      : messages;

    const msgHtml = msgsToprint.map(m => {
      if (m.role === 'user') {
        return `<div class="msg user"><div class="label">Staff</div><div class="bubble user-bubble">${m.content.replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/\n/g,'<br>')}</div></div>`;
      }
      return `<div class="msg bot"><div class="label">AI Assistant</div><div class="bubble bot-bubble">${markdownToHtml(m.content)}</div></div>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>AI Return Chat — ${title}</title>
<style>
  @page {
    size: letter;
    margin: 0.6in 0.65in 0.6in 0.65in;
  }
  *{box-sizing:border-box;margin:0;padding:0}
  body{
    font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif;
    font-size:12.5px;line-height:1.55;color:#1e293b;background:#fff;
    padding:28px 36px;
  }

  /* Header */
  .doc-header{border-bottom:3px solid #1e3a5f;padding-bottom:14px;margin-bottom:20px}
  .doc-title{font-size:18px;font-weight:700;color:#1e3a5f}
  .doc-sub{font-size:11px;color:#64748b;margin-top:3px}
  .doc-meta{font-size:11px;color:#64748b;margin-top:2px}

  /* Messages */
  .msg{margin-bottom:16px}
  .msg.user{page-break-inside:avoid}
  .label{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#64748b;margin-bottom:3px}
  .bubble{padding:10px 14px;border-radius:6px;font-size:12.5px}
  .user-bubble{background:#f0f4ff;border:1px solid #c7d7fc;color:#1e293b;max-width:70%;margin-left:auto}
  .bot-bubble{background:#fff;color:#1e293b}

  /* Markdown in bot bubbles */
  h1,h2,h3{font-weight:700;margin:12px 0 5px}
  h2{font-size:1.05em;color:#1e3a5f;border-bottom:1px solid #e2e8f0;padding-bottom:2px}
  h3{font-size:0.95em;color:#374151}
  p{margin:0 0 7px}
  ul,ol{padding-left:1.5em;margin:0 0 7px}
  li{margin:1px 0}

  /* Tables — print-friendly with proper page breaks */
  table{
    border-collapse:collapse;width:100%;font-size:11.5px;margin:8px 0;
    page-break-inside:auto;
  }
  thead{display:table-header-group} /* repeat header on every page */
  tr{page-break-inside:avoid;page-break-after:auto}
  th{
    background:#f0f4f8;font-weight:600;color:#1e3a5f;text-align:left;
    font-size:10px;text-transform:uppercase;letter-spacing:.04em;
    -webkit-print-color-adjust:exact;print-color-adjust:exact;
  }
  th,td{border:1px solid #dfe4ea;padding:4px 8px;vertical-align:top}
  tr:nth-child(even) td{
    background:#f8fafc;
    -webkit-print-color-adjust:exact;print-color-adjust:exact;
  }

  code{background:#f1f5f9;padding:.1em .3em;border-radius:3px;font-size:85%}
  pre{background:#f8fafc;border:1px solid #e2e8f0;border-radius:5px;padding:8px 12px;margin:6px 0;white-space:pre-wrap;font-size:11.5px}
  strong{font-weight:600}
  blockquote{border-left:3px solid #cbd5e1;padding:6px 12px;margin:8px 0;color:#475569;font-style:italic}

  /* Print overrides */
  @media print{
    body{padding:0}
    .no-print{display:none}
    .bot-bubble{border:none;padding:0}
  }
</style>
</head>
<body>
<div class="doc-header">
  <div class="doc-title">${title}</div>
  <div class="doc-sub">${subLine}</div>
  <div class="doc-meta">AI Return Chat &nbsp;·&nbsp; Printed ${now}${isSingle ? ' &nbsp;·&nbsp; Single response' : ''}</div>
</div>
${msgHtml}
</body>
</html>`;

    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 400);
  }

  /** Minimal markdown→HTML for print output — handles the structures the AI produces. */
  function markdownToHtml(md: string): string {
    return md
      // Tables — convert GFM pipe tables to HTML
      .replace(/(?:^|\n)((?:\|[^\n]+\|\n)+)/g, (_: string, block: string) => {
        const rows = block.trim().split('\n').filter(r => !/^\|[-:| ]+\|$/.test(r.trim()));
        if (!rows.length) return block;
        const toTd = (row: string, tag: string) =>
          '<tr>' + row.split('|').slice(1,-1).map(c => `<${tag}>${c.trim()}</${tag}>`).join('') + '</tr>';
        const [head, ...body] = rows;
        return `<table><thead>${toTd(head,'th')}</thead><tbody>${body.map(r=>toTd(r,'td')).join('')}</tbody></table>`;
      })
      // Headers
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/^## (.+)$/gm,  '<h2>$1</h2>')
      .replace(/^# (.+)$/gm,   '<h1>$1</h1>')
      // Bold / italic
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g,     '<em>$1</em>')
      // Inline code
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      // Bullet lists
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      .replace(/(<li>.*<\/li>)/gs, (_: string, g: string) => `<ul>${g}</ul>`)
      // Numbered lists
      .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
      // Paragraphs — double newline → <p>
      .replace(/\n{2,}/g, '</p><p>')
      .replace(/^(?!<[a-z])/gm, '')
      // Remaining single newlines
      .replace(/\n/g, '<br>');
  }

  // ── Client deliverable ─────────────────────────────────────────────────

  function addPaintSnippet(text: string, msgIdx?: number) {
    if (!text || text.length < 10) return;

    const addHighlight = (snippet: string) => {
      setPaintSnippets(prev => [...prev, snippet]);
      if (msgIdx !== undefined) {
        setPaintHighlights(prev => [...prev, { msgIdx, text }]);
      }
      window.getSelection()?.removeAllRanges();
    };

    // Try to find the markdown source that produced this selected text.
    if (msgIdx !== undefined && messages[msgIdx]?.role === 'assistant') {
      const md = messages[msgIdx].content;
      const mdLines = md.split('\n');

      const norm = (s: string) => s.replace(/[*_#|`>\-=~\[\](){}]/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
      const selLines = text.split('\n').map(l => l.trim()).filter(Boolean);
      if (selLines.length > 0) {
        const firstNorm = norm(selLines[0]).slice(0, 50);
        const lastNorm  = norm(selLines[selLines.length - 1]).slice(-50);

        let startLine = -1, endLine = -1;
        for (let i = 0; i < mdLines.length; i++) {
          const n = norm(mdLines[i]);
          if (startLine === -1 && firstNorm && n.includes(firstNorm)) startLine = i;
          if (lastNorm && n.includes(lastNorm)) endLine = i;
        }
        if (startLine !== -1 && endLine >= startLine) {
          const extracted = mdLines.slice(startLine, endLine + 1).join('\n').trim();
          if (extracted.length >= text.length * 0.5) {
            addHighlight(extracted);
            return;
          }
        }
      }
    }

    addHighlight(text);
  }

  function removePaintSnippet(idx: number) {
    setPaintSnippets(prev => prev.filter((_, i) => i !== idx));
    setPaintHighlights(prev => prev.filter((_, i) => i !== idx));
  }

  async function generateDeliverablePdf(markdown: string) {
    const title = selectedClient?.clientName || returnMeta['RETURN'] || 'Client';
    const et    = selectedReturn?.entityType || returnMeta['Type'] || '';
    const ty    = selectedReturn?.taxYear || returnMeta['TaxYear'] || '';

    showToast('Generating PDF...');
    try {
      const blob = await generatePdfApi({ markdown, clientName: title, entityType: et, taxYear: ty });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${title.replace(/[^a-zA-Z0-9 ._-]/g, '')} - Tax Return Analysis.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast('PDF downloaded.');
    } catch (e: any) {
      showToast(e.message || 'PDF generation failed.');
    }
  }

  async function compilePaintSnippets() {
    if (paintSnippets.length === 0) return;

    const title = selectedClient?.clientName || returnMeta['RETURN'] || 'Client';
    const et    = selectedReturn?.entityType || returnMeta['Type'] || '';
    const ty    = selectedReturn?.taxYear || returnMeta['TaxYear'] || '';
    const snips = [...paintSnippets];

    setPaintMode(false);
    setPaintSnippets([]);
    setPaintHighlights([]);
    showToast('Polishing and generating PDF...');

    try {
      // Step 1: Send to AI for polishing and structuring
      const { markdown } = await polishSnippets({
        snippets: snips,
        clientName: title,
        entityType: et,
        taxYear: ty,
      });

      // Step 2: Generate branded PDF from polished markdown
      const blob = await generatePdfApi({ markdown, clientName: title, entityType: et, taxYear: ty });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${title.replace(/[^a-zA-Z0-9 ._-]/g, '')} - Tax Return Analysis.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast('PDF downloaded.');
    } catch (e: any) {
      showToast(e.message || 'Failed to generate deliverable.');
    }
  }

  function handleStandardDeliverable() {
    setDelivExpanded(false);
    doSend(
      `Produce a polished, client-ready tax return analysis document. This will be printed on firm letterhead and delivered to the client. Format it as a professional deliverable, NOT a chat response.

Structure the document as follows:

## Executive Summary
A 2-3 paragraph overview of the client's tax position this year — key figures, overall outcome, and the most important takeaways.

## Year-over-Year Analysis
A table comparing prior year to current year for all material items (income, deductions, credits, tax, payments, refund/balance due). Include $ change and % change columns. Flag significant variances.

## Key Findings & Observations
Numbered list of the most important items to discuss, organized by:
1. Items requiring client action or decision
2. Planning opportunities for next year
3. Risk areas or items to monitor
4. Notable changes from prior year with explanations

## Tax Planning Recommendations
Specific, actionable recommendations for the upcoming year based on what this return reveals.

## Summary of Tax Position
A concise table showing:
- Total income
- Taxable income
- Total tax liability
- Total payments/withholding
- Refund or balance due
- Effective tax rate

Write in a professional, confident tone appropriate for a CPA firm deliverable. Use precise dollar amounts. Do not include disclaimers about data limitations — present the analysis authoritatively.`
    );
  }

  // ── Derived ───────────────────────────────────────────────────────────────

  const metaTitle = selectedClient?.clientName || returnMeta['RETURN'] || returnId;
  const metaSub   = [selectedReturn?.entityType, selectedReturn?.taxYear].filter(Boolean).join(' · ');

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={s.page}>

      {/* Header */}
      <div style={s.header}>
        <img
          src={`${process.env.PUBLIC_URL}/bot-avatar.png`}
          alt="AI Assistant"
          style={{ ...s.botAvatar, ...((step === 'loading' || introLoading) ? { animation: 'botPensive 1.2s ease-in-out infinite' } : {}) }}
          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
        <div style={s.headerInfo}>
          <div style={s.headerTitle}>
            {step === 'chat' ? metaTitle : 'AI Return Chat'}
          </div>
          <div style={s.headerSub}>
            {step === 'chat'    ? (introLoading ? 'Analyzing your return…' : metaSub || 'Loaded') :
             step === 'loading' ? 'Loading return data…' :
             'Select a return to begin a conversation'}
          </div>
        </div>
        {step === 'chat' && (
          <div style={{ display: 'flex', gap: 6 }}>
            <button style={s.btnSm} onClick={() => { setStep('client'); setMessages([]); setAttachments([]); setUploadError(''); setSearchParams({}, { replace: true }); }}>
              <RefreshCw size={13} style={{ marginRight: 4 }} />New Return
            </button>
          </div>
        )}
      </div>

      {/* Body */}
      <div style={s.body}>

        {/* ── Wizard: client search ── */}
        {step === 'client' && (
          <div style={s.wizard}>
            {/* If a direct link failed (e.g. return in use), show a focused error card first */}
            {directReturnId && loadError && (
              <div style={{ ...s.card, borderColor: 'rgba(248,81,73,.4)', background: 'rgba(248,81,73,.06)', maxWidth: 480 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#f85149', marginBottom: 8 }}>Unable to Load Return</div>
                <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.6 }}>{loadError}</div>
                <button style={{ ...s.btn, marginTop: 14 }} onClick={() => { setLoadError(''); window.location.reload(); }}>
                  Try Again
                </button>
              </div>
            )}
            <div style={s.card}>
              <div style={s.cardTitle}>Search for a Client</div>
              <div style={s.cardSub}>Type at least 3 characters to search by name or code</div>
              <div style={{ position: 'relative' }}>
                <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#64748b', pointerEvents: 'none' }} />
                <input
                  style={{ ...s.input, paddingLeft: 32 }}
                  placeholder="e.g. Smith, 00007-000, etc"
                  value={clientQuery}
                  onChange={e => setClientQuery(e.target.value)}
                  autoFocus
                />
              </div>
              {clientLoading && <div style={{ marginTop: 12, color: 'var(--text-muted)', fontSize: 13 }}><Loader size={13} style={{ marginRight: 6 }} />Searching…</div>}
              {clientError  && <div style={s.error}>{clientError}</div>}
              {loadError    && <div style={s.error}>{loadError}</div>}
              {clients.length > 0 && (
                <div style={s.resultList}>
                  {clients.map(c => (
                    <div key={c.clientCode} style={s.resultItem}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,.07)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      onClick={() => handleClientSelect(c)}>
                      <strong>{c.clientName}</strong>
                      <span style={{ color: 'var(--text-muted)', marginLeft: 8, fontSize: 12 }}>{c.clientCode}</span>
                      {c.partnerName && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{c.partnerName}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Wizard: year picker ── */}
        {step === 'year' && (
          <div style={s.wizard}>
            <div style={s.card}>
              <div style={s.cardTitle}>{selectedClient?.clientName}</div>
              <div style={s.cardSub}>Select a tax year</div>
              <div style={s.yearGrid}>
                {TAX_YEARS.map(y => (
                  <button key={y}
                    style={{ ...s.yearBtn, ...(selectedYear === y ? s.yearBtnSel : {}) }}
                    onClick={() => setSelectedYear(y)}>
                    {y}
                  </button>
                ))}
              </div>
              {returnsError && <div style={s.error}>{returnsError}</div>}
              <button style={s.btn} onClick={handleYearConfirm} disabled={returnsLoading}>
                {returnsLoading ? <Loader size={14} style={{ marginRight: 6 }} /> : null}
                {returnsLoading ? 'Looking up returns…' : 'Continue'}
              </button>
              <button style={{ ...s.btnSm, width: '100%', marginTop: 8, textAlign: 'center' }} onClick={() => setStep('client')}>
                ← Back
              </button>
            </div>
          </div>
        )}

        {/* ── Wizard: return picker (multiple returns) ── */}
        {step === 'return' && (
          <div style={s.wizard}>
            <div style={s.card}>
              <div style={s.cardTitle}>{selectedClient?.clientName} — {selectedYear}</div>
              <div style={s.cardSub}>Select a return</div>
              <div style={s.resultList}>
                {returns.map(r => (
                  <div key={r.returnId} style={s.resultItem}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(100,210,255,.5)')}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border)')}
                    onClick={() => { setSelectedReturn(r); doLoad(r.returnId); }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <strong>{r.returnName || r.returnId}</strong>
                      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{r.entityType}</span>
                      {r.version && (
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 4, background: 'rgba(122,162,255,.15)', color: 'var(--accent)', border: '1px solid rgba(122,162,255,.3)' }}>
                          {r.version}
                        </span>
                      )}
                    </div>
                    {r.description && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{r.description}</div>}
                  </div>
                ))}
              </div>
              {loadError && <div style={s.error}>{loadError}</div>}
              <button style={{ ...s.btnSm, width: '100%', marginTop: 12, textAlign: 'center' }} onClick={() => setStep('year')}>
                ← Back
              </button>
            </div>
          </div>
        )}

        {/* ── Loading ── */}
        {step === 'loading' && (
          <div style={s.loadingBox}>
            <img
              src={`${process.env.PUBLIC_URL}/bot-avatar.png`}
              alt="Loading"
              className="bot-pensive"
              style={s.loadingAvatar}
              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <div style={s.loadingText}>Fetching &amp; parsing return data…</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>This may take 15–30 seconds</div>
          </div>
        )}

        {/* ── Chat ── */}
        {step === 'chat' && (
          <>
            {/* Remove attachment confirm modal */}
            {removeConfirm !== null && (
              <div style={{
                position: 'fixed', inset: 0, zIndex: 10001,
                background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }} onClick={() => setRemoveConfirm(null)}>
                <div
                  style={{
                    background: 'linear-gradient(180deg, #1a2236 0%, #151c2e 100%)',
                    border: '1px solid var(--border)',
                    borderRadius: 14,
                    padding: '24px 28px',
                    maxWidth: 380,
                    width: '90%',
                    boxShadow: '0 12px 40px rgba(0,0,0,.6)',
                    animation: 'tipFadeIn .2s ease',
                  }}
                  onClick={e => e.stopPropagation()}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 10,
                      background: 'rgba(239,68,68,.15)', border: '1px solid rgba(239,68,68,.3)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <X size={18} style={{ color: '#ef4444' }} />
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>Remove Attachment</div>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 8 }}>
                    <span style={{ color: 'var(--text)', fontWeight: 600 }}>{attachments[removeConfirm]?.filename}</span>
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 20 }}>
                    Its content will not be included in future messages. Any prior responses that referenced it will remain in the chat history.
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setRemoveConfirm(null)}
                      style={{
                        padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                        border: '1px solid var(--border)', background: 'rgba(255,255,255,.05)',
                        color: 'var(--text)', cursor: 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmRemoveAttachment}
                      style={{
                        padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                        border: '1px solid rgba(239,68,68,.5)', background: 'rgba(239,68,68,.18)',
                        color: '#ef4444', cursor: 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Cross-ref modal */}
            {crossRefOpen && (
              <div style={{
                position: 'fixed', inset: 0, zIndex: 10001,
                background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }} onClick={closeCrossRef}>
                <div
                  style={{
                    background: 'linear-gradient(180deg, #1a2236 0%, #151c2e 100%)',
                    border: '1px solid var(--border)',
                    borderRadius: 14,
                    padding: '24px 28px',
                    maxWidth: 440,
                    width: '90%',
                    boxShadow: '0 12px 40px rgba(0,0,0,.6)',
                    animation: 'tipFadeIn .2s ease',
                    maxHeight: '80vh',
                    overflowY: 'auto' as const,
                  }}
                  onClick={e => e.stopPropagation()}
                >
                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 10,
                      background: 'rgba(122,162,255,.15)', border: '1px solid rgba(122,162,255,.3)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <GitCompare size={18} style={{ color: 'var(--accent)' }} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>Cross-Reference Return</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)' }}>Load a related return for comparison</div>
                    </div>
                    <button onClick={closeCrossRef} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: 4 }}>
                      <X size={16} />
                    </button>
                  </div>

                  {/* Step: client search */}
                  {crStep === 'client' && (
                    <>
                      <div style={{ position: 'relative', marginBottom: 8 }}>
                        <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#64748b', pointerEvents: 'none' }} />
                        <input
                          style={{ ...s.input, paddingLeft: 32 }}
                          placeholder="Search client name or code…"
                          value={crQuery}
                          onChange={e => setCrQuery(e.target.value)}
                          autoFocus
                        />
                      </div>
                      {crClientLoading && <div style={{ fontSize: 13, color: 'var(--muted)', padding: '8px 0' }}><Loader size={13} style={{ marginRight: 6 }} />Searching…</div>}
                      {crClients.length > 0 && (
                        <div style={{ ...s.resultList, maxHeight: 200 }}>
                          {crClients.map(c => (
                            <div key={c.clientCode} style={s.resultItem}
                              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,.07)')}
                              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                              onClick={() => { setCrSelectedClient(c); setCrStep('year'); }}>
                              <strong>{c.clientName}</strong>
                              <span style={{ color: 'var(--muted)', marginLeft: 8, fontSize: 12 }}>{c.clientCode}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {/* Step: year picker */}
                  {crStep === 'year' && (
                    <>
                      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>{crSelectedClient?.clientName}</div>
                      <div style={s.yearGrid}>
                        {TAX_YEARS.map(y => (
                          <button key={y}
                            style={{ ...s.yearBtn, ...(crYear === y ? s.yearBtnSel : {}) }}
                            onClick={() => setCrYear(y)}>
                            {y}
                          </button>
                        ))}
                      </div>
                      <button style={{ ...s.btn, marginTop: 12 }} onClick={crConfirmYear} disabled={crReturnsLoading}>
                        {crReturnsLoading ? <><Loader size={13} style={{ marginRight: 6 }} />Looking up…</> : 'Continue'}
                      </button>
                      <button style={{ ...s.btnSm, width: '100%', marginTop: 8, textAlign: 'center' }} onClick={() => setCrStep('client')}>
                        ← Back
                      </button>
                    </>
                  )}

                  {/* Step: return picker */}
                  {crStep === 'return' && (
                    <>
                      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>{crSelectedClient?.clientName} — {crYear}</div>
                      <div style={{ ...s.resultList, maxHeight: 200 }}>
                        {crReturns.map(r => (
                          <div key={r.returnId} style={s.resultItem}
                            onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(100,210,255,.5)')}
                            onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border)')}
                            onClick={() => crLoadReturn(r.returnId)}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <strong>{r.returnName || r.returnId}</strong>
                              <span style={{ color: 'var(--muted)', fontSize: 12 }}>{r.entityType}</span>
                              {r.version && <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 4, background: 'rgba(122,162,255,.15)', color: 'var(--accent)', border: '1px solid rgba(122,162,255,.3)' }}>{r.version}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                      <button style={{ ...s.btnSm, width: '100%', marginTop: 12, textAlign: 'center' }} onClick={() => setCrStep('year')}>
                        ← Back
                      </button>
                    </>
                  )}

                  {/* Step: loading */}
                  {crStep === 'loading' && (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                      <img
                        src={`${process.env.PUBLIC_URL}/bot-avatar.png`}
                        alt="Loading"
                        className="bot-pensive"
                        style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' as const, marginBottom: 10 }}
                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                      <div style={{ fontSize: 13, color: 'var(--muted)' }}>Loading cross-reference data…</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Extracting key sections (summary, K-1s, payments)</div>
                    </div>
                  )}

                  {crError && <div style={{ ...s.error, marginTop: 8 }}>{crError}</div>}
                </div>
              </div>
            )}

            {/* Share email modal */}
            {shareModalOpen && (
              <div style={{
                position: 'fixed', inset: 0, zIndex: 10001,
                background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }} onClick={() => !shareSending && setShareModalOpen(false)}>
                <div
                  style={{
                    background: 'linear-gradient(180deg, #1a2236 0%, #151c2e 100%)',
                    border: '1px solid var(--border)',
                    borderRadius: 14,
                    padding: '24px 28px',
                    maxWidth: 480,
                    width: '90%',
                    boxShadow: '0 12px 40px rgba(0,0,0,.6)',
                    animation: 'tipFadeIn .2s ease',
                  }}
                  onClick={e => e.stopPropagation()}
                >
                  {shareSuccess ? (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                      <div style={{ fontSize: 32, marginBottom: 10 }}>&#x2705;</div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>Chat Shared</div>
                      <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 6 }}>Email sent. A copy was BCC'd to you.</div>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: 10,
                          background: 'rgba(122,162,255,.15)', border: '1px solid rgba(122,162,255,.3)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                          <Mail size={18} style={{ color: 'var(--accent)' }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>Share Chat via Email</div>
                          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Recipient will see a read-only view</div>
                        </div>
                        <button onClick={() => setShareModalOpen(false)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: 4 }}>
                          <X size={16} />
                        </button>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em', display: 'block', marginBottom: 4 }}>To *</label>
                          <input
                            style={s.input}
                            value={shareTo}
                            onChange={e => setShareTo(e.target.value)}
                            placeholder="recipient@bmss.com (separate multiple with commas)"
                            autoFocus
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em', display: 'block', marginBottom: 4 }}>Subject</label>
                          <input
                            style={s.input}
                            value={shareSubject}
                            onChange={e => setShareSubject(e.target.value)}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em', display: 'block', marginBottom: 4 }}>Message</label>
                          <textarea
                            style={{ ...s.input, height: 80, resize: 'vertical' as const, lineHeight: 1.5, fontFamily: 'inherit' }}
                            value={shareBody}
                            onChange={e => setShareBody(e.target.value)}
                          />
                        </div>

                        <div style={{ background: 'rgba(122,162,255,.08)', border: '1px solid rgba(122,162,255,.2)', borderRadius: 8, padding: '8px 12px', fontSize: 11, color: 'var(--muted)', lineHeight: 1.5 }}>
                          &#x1F512; The chat will be shared as <strong style={{ color: 'var(--text)' }}>read-only</strong>. You will be BCC'd on the email.
                        </div>

                        {shareError && <div style={{ fontSize: 12, color: '#ef4444' }}>{shareError}</div>}

                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => setShareModalOpen(false)}
                            disabled={shareSending}
                            style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid var(--border)', background: 'rgba(255,255,255,.05)', color: 'var(--text)', cursor: 'pointer', fontFamily: 'inherit' }}
                          >Cancel</button>
                          <button
                            onClick={handleShareSend}
                            disabled={shareSending || !shareTo.trim()}
                            style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid rgba(122,162,255,.5)', background: 'rgba(122,162,255,.18)', color: 'var(--accent)', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6, opacity: shareSending || !shareTo.trim() ? .5 : 1 }}
                          >
                            {shareSending ? <Loader size={13} /> : <Send size={13} />}
                            {shareSending ? 'Sending…' : 'Send Email'}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Toast notification */}
            {toast && (
              <div style={{
                position: 'fixed', bottom: 90, left: '50%', transform: 'translateX(-50%)',
                zIndex: 10000, background: '#1e293b', color: '#f1f5f9',
                border: '1px solid rgba(122,162,255,.35)', borderRadius: 10,
                padding: '10px 18px', fontSize: 13, fontWeight: 500,
                boxShadow: '0 6px 24px rgba(0,0,0,.55)',
                display: 'flex', alignItems: 'center', gap: 8,
                animation: 'tipFadeIn .25s ease',
              }}>
                <Paperclip size={14} style={{ color: 'var(--accent)', flexShrink: 0 }} />
                {toast}
                <button onClick={() => setToast(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '0 0 0 6px', fontSize: 15, lineHeight: 1 }}>×</button>
              </div>
            )}

            {/* Selection popup — portaled to page root so it floats above everything */}
            {selPopup && (
              <div
                ref={selPopupRef}
                style={{
                  position:     'fixed',
                  zIndex:       9999,
                  left:         selPopup.x,
                  top:          selPopup.y - 8,
                  transform:    'translateX(-50%) translateY(-100%)',
                  background:   '#0f172a',
                  border:       paintMode ? '1px solid rgba(108,194,74,.5)' : '1px solid rgba(122,162,255,.45)',
                  borderRadius: 10,
                  padding:      '5px 6px',
                  display:      'flex',
                  gap:          4,
                  boxShadow:    '0 6px 24px rgba(0,0,0,.65)',
                  pointerEvents: 'auto',
                }}
              >
                {paintMode ? (
                  <button
                    style={{ padding: '5px 12px', borderRadius: 7, border: '1px solid rgba(108,194,74,.4)', background: 'rgba(108,194,74,.18)', color: '#6CC24A', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'inherit', whiteSpace: 'nowrap' }}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => { addPaintSnippet(selPopup.text, selPopup.msgIdx); setSelPopup(null); }}
                  >
                    <Paintbrush size={12} /> Add to Deliverable
                  </button>
                ) : (
                  <>
                    <button
                      style={{ padding: '5px 10px', borderRadius: 7, border: '1px solid rgba(122,162,255,.35)', background: 'rgba(122,162,255,.15)', color: 'var(--accent)', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'inherit', whiteSpace: 'nowrap' }}
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => handleDigIn(selPopup.text)}
                    >
                      <ZoomIn size={12} /> Dig into this
                    </button>
                    <button
                      style={{ padding: '5px 10px', borderRadius: 7, border: '1px solid var(--border)', background: 'rgba(255,255,255,.05)', color: 'var(--text)', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'inherit', whiteSpace: 'nowrap' }}
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => handleAskAbout(selPopup.text)}
                    >
                      <MessageSquare size={12} /> Ask about this
                    </button>
                  </>
                )}
              </div>
            )}

            <div style={s.messages} onMouseUp={handleMessagesMouseUp} className={paintMode ? 'paint-mode-active' : undefined}>
              {messages.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)', fontSize: 14 }}>
                  <img
                    src={`${process.env.PUBLIC_URL}/bot-avatar.png`}
                    alt=""
                    className={introLoading ? 'bot-pensive' : undefined}
                    style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', marginBottom: 12, opacity: .85 }}
                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                  {introLoading
                    ? <>
                        <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text)' }}>Analyzing your return…</div>
                        <div>Running two-year review · This may take a moment</div>
                      </>
                    : <>
                        <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text)' }}>Ready to review</div>
                        <div>{viewNames.length} sections loaded · Ask anything about this return</div>
                      </>
                  }
                </div>
              )}

              {messages.map((msg, i) => {
                const isUser = msg.role === 'user';
                const isLast = i === messages.length - 1;
                return (
                  <div key={i}
                    data-msg-idx={i}
                    style={{
                      ...s.msgRow,
                      ...(isUser ? s.msgRowUser : {}),
                      alignSelf: isUser ? 'flex-end' : 'flex-start',
                    }}
                  >
                    {!isUser && (
                      <img
                        src={`${process.env.PUBLIC_URL}/bot-avatar.png`}
                        alt="Bot"
                        style={s.msgAvatar}
                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    )}
                    <div style={{ ...s.msgBubble, ...(isUser ? s.msgBubbleUser : {}), maxWidth: isUser ? 540 : '100%' }}>
                      {!isUser && !paintMode && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          {isLast && lastScoped && mode === 'auto'
                            ? <span style={s.scopeTag}>
                                <Zap size={10} />Scoped to {lastScoped.length} of {viewNames.length} sections
                                {lastPriorYear && <> · <Database size={10} /> Prior year included</>}
                              </span>
                            : <span />}
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button
                              style={{ ...s.modeBtn, padding: '3px 7px', fontSize: 11 }}
                              title="Print this response"
                              onClick={() => printChat(i)}>
                              <Printer size={11} />
                            </button>
                            {hasPermission(PERMISSIONS.FEATURE_DELIVERABLE) && <button
                              style={{ ...s.modeBtn, padding: '3px 7px', fontSize: 11 }}
                              title="Download this response as branded PDF"
                              onClick={() => generateDeliverablePdf(msg.content)}>
                              <FileDown size={11} />
                            </button>}
                          </div>
                        </div>
                      )}
                      {isUser ? (
                        <span style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</span>
                      ) : (
                        <div className="chat-md">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {sending && (
                <div style={{ ...s.msgRow, alignSelf: 'flex-start' }}>
                  <img
                    src={`${process.env.PUBLIC_URL}/bot-avatar.png`}
                    alt="Bot"
                    className="bot-pensive"
                    style={s.msgAvatar}
                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                  <div style={s.msgBubble}>
                    <span style={s.thinkingDot}>•••</span>
                  </div>
                </div>
              )}

              {sendError && (
                <div style={{ color: '#ef4444', fontSize: 13, padding: '4px 8px' }}>{sendError}</div>
              )}

              {/* Paint mode floating toolbar — fixed at bottom of viewport */}
              {paintMode && (
                <div style={{
                  position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9998,
                  background: '#0f172a', borderTop: '2px solid #6CC24A',
                  padding: '10px 16px', boxShadow: '0 -4px 20px rgba(0,0,0,.5)',
                  animation: 'tipFadeIn .2s ease',
                }}>
                  {/* Header bar */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: paintSnippets.length > 0 ? 8 : 0 }}>
                    <Paintbrush size={14} style={{ color: '#6CC24A' }} />
                    <span style={{ fontSize: 12, color: '#6CC24A', fontWeight: 600 }}>
                      {paintSnippets.length === 0 ? 'Highlight text in any response to add to your deliverable' : `${paintSnippets.length} selection${paintSnippets.length !== 1 ? 's' : ''}`}
                    </span>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                      <button
                        className="chat-action-btn"
                        style={{
                          padding: '5px 14px', borderRadius: 16, fontSize: 11, fontWeight: 600,
                          border: '1px solid rgba(108,194,74,.5)', background: 'rgba(108,194,74,.18)',
                          color: '#6CC24A', cursor: paintSnippets.length > 0 ? 'pointer' : 'not-allowed',
                          fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4,
                          opacity: paintSnippets.length > 0 ? 1 : .4,
                        }}
                        disabled={paintSnippets.length === 0}
                        onClick={compilePaintSnippets}
                      >
                        <FileDown size={11} /> Compile PDF
                      </button>
                      <button
                        onClick={() => { setPaintMode(false); setPaintSnippets([]); setPaintHighlights([]); }}
                        style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: '0 6px', fontSize: 16, fontWeight: 700 }}
                        title="Exit paint mode"
                      >×</button>
                    </div>
                  </div>
                  {/* Snippet chips */}
                  {paintSnippets.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 100, overflowY: 'auto' }}>
                      {paintSnippets.map((snip, i) => (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'flex-start', gap: 6,
                          padding: '5px 10px', borderRadius: 8,
                          background: 'rgba(108,194,74,.08)', border: '1px solid rgba(108,194,74,.25)',
                          fontSize: 11, color: 'var(--muted)', lineHeight: 1.4,
                        }}>
                          <span style={{ color: '#6CC24A', fontWeight: 700, flexShrink: 0, fontSize: 10, marginTop: 1 }}>{i + 1}</span>
                          <span style={{ flex: 1, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const }}>
                            {snip.length > 150 ? snip.slice(0, 150) + '…' : snip}
                          </span>
                          <button
                            onClick={() => removePaintSnippet(i)}
                            style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: 0, flexShrink: 0, marginTop: 1 }}
                            title="Remove this selection"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Selection feature hint — always visible below the latest response */}
              {messages.length > 0 && !sending && !paintMode && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 13px', borderRadius: 20, background: 'rgba(122,162,255,.1)', border: '1px solid rgba(122,162,255,.25)', fontSize: 12, color: 'var(--muted)', maxWidth: 420 }}>
                    <ZoomIn size={13} style={{ color: 'var(--accent)', flexShrink: 0 }} />
                    <span><strong style={{ color: 'var(--text)', fontWeight: 600 }}>Tip:</strong> Select any text in a response to <span style={{ color: 'var(--accent)' }}>dig deeper</span> or <span style={{ color: 'var(--accent)' }}>ask a follow-up</span></span>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Read-only banner for shared chats */}
            {readOnly && (
              <div style={{ padding: '8px 16px', background: 'rgba(122,162,255,.08)', borderTop: '1px solid rgba(122,162,255,.2)', textAlign: 'center', fontSize: 12, color: 'var(--muted)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                &#x1F512; <strong style={{ color: 'var(--text)' }}>Read-only</strong> — This is a shared chat view.
                <button
                  style={{ ...s.promptBtn, display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 8 }}
                  onClick={() => printChat('all')}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(122,162,255,.55)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--accent)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--muted)'; }}
                >
                  <Printer size={10} /> Print
                </button>
                <button
                  style={{ ...s.promptBtn, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  onClick={openShareModal}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(122,162,255,.55)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--accent)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--muted)'; }}
                >
                  <Mail size={10} /> Forward
                </button>
                {hasPermission(PERMISSIONS.FEATURE_DELIVERABLE) && (
                <button
                  style={{ ...s.promptBtn, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  onClick={() => { setPaintMode(true); setPaintSnippets([]); setPaintHighlights([]); }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(108,194,74,.55)'; (e.currentTarget as HTMLButtonElement).style.color = '#6CC24A'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--muted)'; }}
                >
                  <Paintbrush size={10} /> Deliverable
                </button>
                )}
              </div>
            )}

            {/* Power prompt strip + attachments + input bar — hidden in read-only mode */}
            {!readOnly && (<>
            <div style={s.promptStrip}>
              {POWER_PROMPTS.filter(p => {
                if (!p.entityTypes) return true;
                const et = (selectedReturn?.entityType || returnMeta['Type'] || '').toString().trim().toUpperCase();
                return p.entityTypes.includes(et);
              }).map(p => (
                <button
                  key={p.label}
                  style={s.promptBtn}
                  disabled={sending}
                  onMouseEnter={e => {
                    if (!sending) {
                      (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(122,162,255,.55)';
                      (e.currentTarget as HTMLButtonElement).style.color = 'var(--accent)';
                      (e.currentTarget as HTMLButtonElement).style.background = 'rgba(122,162,255,.12)';
                    }
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)';
                    (e.currentTarget as HTMLButtonElement).style.color = 'var(--muted)';
                    (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,.04)';
                  }}
                  onClick={() => doSend(p.prompt)}
                  title={p.prompt}
                >
                  {p.label}
                </button>
              ))}
              {messages.length > 0 && (
                <>
                <button
                  style={{ ...s.promptBtn, marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}
                  onClick={openCrossRef}
                  title="Load a related return for cross-reference analysis"
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(122,162,255,.55)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--accent)'; (e.currentTarget as HTMLButtonElement).style.background = 'rgba(122,162,255,.12)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--muted)'; (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,.04)'; }}
                >
                  <GitCompare size={11} /> Cross-Ref
                </button>
                {/* Deliverable — expands to Standard + Custom (permission-gated) */}
                {hasPermission(PERMISSIONS.FEATURE_DELIVERABLE) && <div style={{ position: 'relative', flexShrink: 0 }}>
                  {!delivExpanded ? (
                    <button
                      style={{ ...s.promptBtn, display: 'flex', alignItems: 'center', gap: 4 }}
                      onClick={() => setDelivExpanded(true)}
                      title="Create a client-ready deliverable document"
                      onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(108,194,74,.55)'; (e.currentTarget as HTMLButtonElement).style.color = '#6CC24A'; (e.currentTarget as HTMLButtonElement).style.background = 'rgba(108,194,74,.12)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--muted)'; (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,.04)'; }}
                    >
                      <FileDown size={11} /> Deliverable
                    </button>
                  ) : (
                    <div style={{ display: 'flex', gap: 4, animation: 'tipFadeIn .15s ease' }}>
                      <button
                        style={{ ...s.promptBtn, display: 'flex', alignItems: 'center', gap: 4 }}
                        onClick={handleStandardDeliverable}
                        title="AI generates a complete client-ready analysis document"
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(108,194,74,.55)'; (e.currentTarget as HTMLButtonElement).style.color = '#6CC24A'; (e.currentTarget as HTMLButtonElement).style.background = 'rgba(108,194,74,.12)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--muted)'; (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,.04)'; }}
                      >
                        <FileDown size={11} /> Standard
                      </button>
                      <button
                        style={{ ...s.promptBtn, display: 'flex', alignItems: 'center', gap: 4 }}
                        onClick={() => { setPaintMode(true); setPaintSnippets([]); setPaintHighlights([]); setDelivExpanded(false); }}
                        title="Select specific responses to compile into a deliverable"
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(108,194,74,.55)'; (e.currentTarget as HTMLButtonElement).style.color = '#6CC24A'; (e.currentTarget as HTMLButtonElement).style.background = 'rgba(108,194,74,.12)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--muted)'; (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,.04)'; }}
                      >
                        <Paintbrush size={11} /> Custom
                      </button>
                      <button
                        style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: '0 2px', fontSize: 14 }}
                        onClick={() => setDelivExpanded(false)}
                        title="Collapse"
                      >×</button>
                    </div>
                  )}
                </div>}
                {/* Share — expands to show Print + Email options */}
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  {!shareExpanded ? (
                    <button
                      style={{ ...s.promptBtn, display: 'flex', alignItems: 'center', gap: 4 }}
                      onClick={() => setShareExpanded(true)}
                      title="Share or print this chat"
                      onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(122,162,255,.55)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--accent)'; (e.currentTarget as HTMLButtonElement).style.background = 'rgba(122,162,255,.12)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--muted)'; (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,.04)'; }}
                    >
                      <Share2 size={11} /> Share
                    </button>
                  ) : (
                    <div style={{ display: 'flex', gap: 4, animation: 'tipFadeIn .15s ease' }}>
                      <button
                        style={{ ...s.promptBtn, display: 'flex', alignItems: 'center', gap: 4 }}
                        onClick={() => { printChat('all'); setShareExpanded(false); }}
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(122,162,255,.55)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--accent)'; (e.currentTarget as HTMLButtonElement).style.background = 'rgba(122,162,255,.12)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--muted)'; (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,.04)'; }}
                      >
                        <Printer size={11} /> Print
                      </button>
                      <button
                        style={{ ...s.promptBtn, display: 'flex', alignItems: 'center', gap: 4 }}
                        onClick={() => { openShareModal(); }}
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(122,162,255,.55)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--accent)'; (e.currentTarget as HTMLButtonElement).style.background = 'rgba(122,162,255,.12)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--muted)'; (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,.04)'; }}
                      >
                        <Mail size={11} /> Email
                      </button>
                      <button
                        style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: '0 2px', fontSize: 14 }}
                        onClick={() => setShareExpanded(false)}
                        title="Collapse"
                      >×</button>
                    </div>
                  )}
                </div>
                </>
              )}
            </div>

            {/* Attachment chips — only visible when files are attached */}
            {(attachments.length > 0 || uploadingFile || uploadError) && (
              <div style={{ padding: '4px 16px 0', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', flexShrink: 0 }}>
                {attachments.map((att, i) => (
                  <div key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 16, background: 'rgba(87,211,140,.12)', border: '1px solid rgba(87,211,140,.4)', fontSize: 12, color: 'var(--good)' }}>
                    {att.type === 'image' ? <Image size={12} /> : <FileText size={12} />}
                    <span style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.filename}</span>
                    {att.truncated && <span title="File was too large and has been truncated" style={{ color: '#f59e0b', fontSize: 10, fontWeight: 700 }}>TRUNC</span>}
                    <button onClick={() => removeAttachment(i)} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: 0, display: 'flex', lineHeight: 1 }} title="Remove attachment">
                      <X size={12} />
                    </button>
                  </div>
                ))}
                {uploadingFile && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 16, background: 'rgba(122,162,255,.1)', border: '1px dashed rgba(122,162,255,.4)', fontSize: 12, color: 'var(--accent)' }}>
                    <Loader size={12} style={{ animation: 'chatDotPulse 1.4s infinite' }} />
                    Uploading…
                  </div>
                )}
                {uploadError && <span style={{ fontSize: 12, color: '#ef4444' }}>{uploadError}</span>}
              </div>
            )}

            {/* Input bar */}
            <div style={s.inputBar}>
              {/* Mode toggle */}
              <div style={s.modeToggle}>
                <button
                  style={{ ...s.modeBtn, ...(mode === 'auto' ? s.modeBtnOn : {}) }}
                  title="Auto-scope: AI selects relevant sections"
                  onClick={() => setMode('auto')}>
                  <Zap size={12} />Auto
                </button>
                <button
                  style={{ ...s.modeBtn, ...(mode === 'full' ? s.modeBtnOn : {}) }}
                  title="Full context: send all return data"
                  onClick={() => setMode('full')}>
                  <Database size={12} />Full
                </button>
              </div>

              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0, minWidth: 0 }}>
                {/* Quote context chip */}
                {quoteContext && (
                  <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: 8,
                    padding: '7px 11px', borderRadius: '10px 10px 0 0',
                    background: 'rgba(0,0,0,.25)', borderBottom: 'none',
                    border: '1px solid var(--border)', borderBottomColor: 'transparent',
                  }}>
                    <div style={{
                      flex: 1, fontSize: 12, color: 'var(--muted)', lineHeight: 1.5,
                      borderLeft: '2px solid var(--accent)', paddingLeft: 8,
                      maxHeight: 60, overflow: 'hidden',
                    }}>
                      <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--accent)', display: 'block', marginBottom: 1 }}>Replying to</span>
                      {quoteContext.length > 150 ? quoteContext.slice(0, 150) + '…' : quoteContext}
                    </div>
                    <button
                      onClick={() => setQuoteContext(null)}
                      style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: 0, flexShrink: 0, display: 'flex', marginTop: 2 }}
                      title="Remove quote"
                    >
                      <X size={13} />
                    </button>
                  </div>
                )}
                <textarea
                  ref={textareaRef}
                  style={{
                    ...s.textarea,
                    ...(isListening ? { borderColor: 'rgba(239,68,68,.45)' } : {}),
                    ...(quoteContext ? { borderTopLeftRadius: 0, borderTopRightRadius: 0, borderTop: '1px solid var(--border)' } : {}),
                  }}
                  placeholder={isListening ? 'Listening… speak now' : quoteContext ? 'Add your question…' : 'Ask about this return… (Enter to send, Shift+Enter for new line)'}
                  value={isListening ? input + (interimText ? (input && !input.endsWith(' ') && !input.endsWith('\n') ? ' ' : '') + interimText : '') : input}
                  onChange={e => { if (!isListening) setInput(e.target.value); }}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  rows={1}
                  disabled={sending}
                />
              </div>
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_FILES}
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <button
                className="chat-action-btn"
                style={{
                  ...s.micBtn,
                  position: 'relative' as const,
                  opacity: (sending || uploadingFile || attachments.length >= MAX_ATTACHMENTS) ? .35 : 1,
                  cursor: attachments.length >= MAX_ATTACHMENTS ? 'not-allowed' : 'pointer',
                }}
                title={attachments.length >= MAX_ATTACHMENTS
                  ? `Max ${MAX_ATTACHMENTS} files attached — remove one to add another`
                  : 'Attach a file (PDF, Excel, CSV, or image)\nTip: For Excel files, screenshots often give better results than raw .xlsx uploads'}
                disabled={sending || uploadingFile}
                onClick={() => {
                  if (attachments.length >= MAX_ATTACHMENTS) {
                    showToast(`Maximum ${MAX_ATTACHMENTS} files — remove one to add another.`);
                  } else {
                    fileInputRef.current?.click();
                  }
                }}
              >
                <Paperclip size={18} />
                {attachments.length > 0 && (
                  <span style={{
                    position: 'absolute', top: -4, right: -4,
                    width: 16, height: 16, borderRadius: '50%',
                    background: attachments.length >= MAX_ATTACHMENTS ? '#ef4444' : '#57d38c',
                    color: '#fff', fontSize: 10, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    lineHeight: 1,
                  }}>
                    {attachments.length}
                  </span>
                )}
              </button>
              {micSupported && (
                <button
                  className="chat-action-btn"
                  style={{ ...s.micBtn, ...(isListening ? s.micBtnOn : {}), opacity: sending ? .4 : 1 }}
                  title={isListening ? 'Stop recording (or wait 3s for auto-stop)' : 'Dictate your message'}
                  disabled={sending}
                  onClick={handleMicClick}
                >
                  <Mic size={18} />
                </button>
              )}
              <button className="chat-send-btn" style={{ ...s.sendBtn, opacity: sending || !(input + interimText).trim() ? .5 : 1 }}
                onClick={handleSend} disabled={sending || !(input + interimText).trim()}>
                {sending ? <Loader size={18} /> : <Send size={18} />}
              </button>
            </div>
            </>)}
          </>
        )}
      </div>
    </div>
  );
}
